import React, { Component } from 'react';
import { Button, Table } from 'react-bootstrap';
import classNames from "classnames";
import _ from 'underscore';
import { Label } from '@diversioteam/diversio-ds'
import Filters, {Year, Office, Level, Department, Tenure, FiltersSelected, FilterContext} from '../Filters';
import "./index.scss";
import {industryOrRegional, mapChoiceLabel} from "../../../utils";
import { _makeCell } from "../../Home/HeatMap/utils";
import {
    getOfficeRegionLabel,
    getTenureLabel,
    mapMetaKpiLabels,
    getDepartmentLabel,
    getLevelLabel,
    getDominantGroupCellLabel,
    getYearLabel,
} from "../../../utils/labels";
import { withTranslation } from 'react-i18next';
import MainCard from "../../utils/MainCard";
import Legend from './Legend';
import HeatmapSnapshot from "./Snapshot";
import ReactTooltip from 'react-tooltip';
import * as m from '../../../translations/mapping';
import { mapT } from '../../../translations/utils';
import mixpanel from "mixpanel-browser";
import {createFiltersPayload, getYearAliasMap} from "../Filters/utils";
import i18n from "../../../i18n";
import { DashboardTooltip } from '../../utils/DashboardTooltip';
import TAXONOMIES from '../../../utils/taxonomies';

/* Table columns in desired order of appearance */
const COLUMN_KEYS = [
    'INCLUSIVE_CULTURE',
    'FAIR_MANAGEMENT',
    'CAREER_DEVELOPMENT',
    'WORKPLACE_FLEXIBILITY',
    'WORKPLACE_SAFETY',
    'RECRUITING_HIRING'
];

class HeatMap extends Component {
    constructor(props) {
        super(props);
        this.state = {
            isHover: 'normal',
            row: "-1",
            column: '-1',
            selectedFilters: {},
            tab: "overview",
            currentPage: 1,
            dominantGroups: props.allFilters.dominant_group
        };
    };

    setCurrentPage = (page) => {
        this.setState({
            currentPage: page || 1,
        })
    }

    onHover = (r, c) => {
        this.setState({
            isHover: 'hover',
            row: r,
            column: c
        })
    };

    offHover = () => {
        this.setState({
            isHover: 'hoverEnter',
            row: "-1",
            column: "-1"
        })
    };

    _resetData () {
        this.props.getInclusionMetricsData({data: {year: "", level: "", office: "", department: ""}});
    }

    componentDidMount() {
        this._resetData();
    }

    selectTab = tab => {
        this.setState({
            ...this.state,
            tab
        });

        if (tab === "overview") {
            // TODO @sahanarula this can't be called w empty payload
            // not sure how to refer to selected filters here
            this._resetData();
        } else {
            this.props.getNonOverviewMetricsData({ data: { key: tab } });

            // reset the currentPage in Snapshot to 1,
            // in case user has scrolled using the navigation arrows
            this.setCurrentPage(1);
        }
    };

    handleApplyFilters = (data, filters) => {
        this.setState({
            dominantGroups: filters.dominant_group
        })
        if (this.state.tab === "overview") {
            this.props.getInclusionMetricsData({ data });
        } else {
            this.props.getNonOverviewMetricsData({ data: { ...data, key: this.state.tab } });
        }
    };

    handleClearFilters = data => {
        this._resetData();
    }

    hoverEnter = (row, column) => {
        this.onHover(row, column);
    };

    track (selectedTab) {
        mixpanel.track(TAXONOMIES.DOWNLOAD_CSV, {
            csvClickedAt: new Date().toISOString(),
            selectedTab: "overview"
        })
    }

    _handleExcelExport = () => {
        if (this.state.tab === "overview") {
            this.props.getInclusionMetricsExcel({ data: {...createFiltersPayload(this.state.selectedFilters, this.props.allFilters), export_format: "excel", lang: i18n.language} });
            this.track("Overview")
        } else {
            this.props.getNonOverviewMetricsExcel({ data: { ...this.state.selectedFilters, key: this.state.tab, export_format: "excel", lang: i18n.language } });
            this.track("Non-Overview")
        }
    };

    getDgTooltipContent = (companyName) => {
        const dgItems = this.state.dominantGroups.available_descriptors
        const hasPartial = dgItems && dgItems.some((item) => item.includes("partial"))
        const dominantGroupLabel = getDominantGroupCellLabel(companyName, "Dominant Group");
        let content = `${dominantGroupLabel} refers to the combined demographics most represented in senior levels at your organization` + (hasPartial ? ('; subject to the country’s data restriction policies.\n\nAny group denoted "partial" has data unavailable for at least 1 survey respondent.') : '.');
        if (dominantGroupLabel === "Majority Group"){
           content = `${dominantGroupLabel} represented in senior executive levels`;
        }

        return (
            <>  
                <div className='datatable__dg-tooltip-labels'>
                    {dgItems && dgItems.map(field =>
                        <Label text={field} type="secondary" />
                    )}
                </div>
                
                <div className='datatable__dg-tooltip-description'>
                    {content}
                </div>

                <div className='datatable__dg-tooltip-description'>
                    {this.state.dominantGroups.help_text}
                </div>
            </>
        )
    }

    render() {
        const { metadata = [], companyName, t, data, prevData, overall, previousOverall, loadingNonOverviewMetrics, isExportToExcelEnabled } = this.props;
        const { tab } = this.state;

        const disableMyKpi = (tab !== "overview");

        return (
            <MainCard title={t("ANALYZE.CARD_HEADERS.HEATMAP.title")}
                      cypressId={"inclusion-heat-map"}
                      toolTipContent={t("ANALYZE.CARD_HEADERS.HEATMAP.tooltip")}>
            <div id={"heat-map"} className={"bootstrap-override"}>
                <Filters
                    hidden={tab !== "overview"}
                    filters={this.props.allFilters}
                    applyFilters={this.handleApplyFilters.bind(this)}
                    clearFilters={this.handleClearFilters.bind(this)}
                    yearAliasMap={getYearAliasMap(companyName)}
                    companyName={companyName}>
                    <Year label={getYearLabel(companyName)}/>
                    <Office label={getOfficeRegionLabel(companyName)}/>
                    <Level label={getLevelLabel(companyName)} />
                    <Department label={getDepartmentLabel(companyName)}/>
                    <Tenure label={getTenureLabel(companyName)}/>
                    {
                        <FilterContext.Consumer>
                            {data => {
                                if (this.state.selectedFilters !== data.values) {
                                    this.setState({
                                        ...this.state,
                                        selectedFilters: data.values
                                    })
                                }
                            }}
                        </FilterContext.Consumer>
                    }
                </Filters>
                <HeatmapSnapshot title={tab}
                                 companyName={companyName}
                                 data={data}
                                 prevData={prevData}
                                 overall={overall}
                                 previousOverall={previousOverall}
                                currentPage={this.state.currentPage}
                                setCurrentPage={this.setCurrentPage}
                                loadingNonOverviewMetrics={loadingNonOverviewMetrics}/>

                <Button className={"export-excel-btn"} onClick={this._handleExcelExport}>Download Excel</Button>

                <hr/>
                <div className='datatable-container'>
                    <Table className={"datatable"}>
                        <thead className="datatable__scrollable-table-head">
                            <tr className={"datatable-filters"}>
                                <th onClick={() => {
                                    mixpanel.track(TAXONOMIES.SELECT_TAB, {
                                        name: "Inclusion Heatmap",
                                        tabname: "overview"
                                    })
                                    this.selectTab("overview")
                                }}
                                className={classNames("tab", {"selected": tab === "overview"})}>{t("ANALYZE.FILTERS.OVERVIEW")}</th>
                                <th onClick={() => {
                                    mixpanel.track(TAXONOMIES.SELECT_TAB, {
                                        name: "Inclusion Heatmap",
                                        tabname: "department"
                                    })
                                    this.selectTab("department")
                                }} className={classNames("tab", {"selected":  tab === "department"})}>{getDepartmentLabel(companyName)}</th>
                                <th onClick={() => {
                                    mixpanel.track(TAXONOMIES.SELECT_TAB, {
                                        name: "Inclusion Heatmap",
                                        tabname: "office"
                                    })
                                    this.selectTab("office")
                                }} className={classNames("tab", {"selected":  tab === "office"})}>{getOfficeRegionLabel(companyName)}</th>
                                <th onClick={() => {
                                    mixpanel.track(TAXONOMIES.SELECT_TAB, {
                                        name: "Inclusion Heatmap",
                                        tabname: "level"
                                    })
                                    this.selectTab("level")
                                }} className={classNames("tab", {"selected":  tab === "level"})}>{getLevelLabel(companyName)}</th>
                                <th onClick={() => {
                                    mixpanel.track(TAXONOMIES.SELECT_TAB, {
                                        name: "Inclusion Heatmap",
                                        tabname: "year"
                                    })
                                    this.selectTab("year")
                                }} className={classNames("tab", {"selected":  tab === "year"})}>{t("ANALYZE.FILTERS.YEAR")}</th>
                                <th onClick={() => {
                                    mixpanel.track(TAXONOMIES.SELECT_TAB, {
                                        name: "Inclusion Heatmap",
                                        tabname: "tenure"
                                    })
                                    this.selectTab("tenure")
                                }} className={classNames("tab", {"selected":  tab === "tenure"})}>{t("ANALYZE.FILTERS.TENURE")}</th>
                                <th className={"empty-cell"}></th>
                            </tr>
                            <tr className={"datatable-border"}></tr>
                            <tr className={"datatable-metric"}>
                                <th>
                                {
                                    data.length ? null : "No results."
                                }
                                </th>
                                {   data.length ?
                                    metadata.map((meta, id) =>
                                    (<th key={id}>

                                    {/* <DashboardTooltip content={mapT(m.METRIC_TO_TOOLTIP, meta.name, companyName)}> */}
                                    {/* Note that the DashboardTooltip does not render with correct position here */}
                                    <a data-tip data-for={`tooltip-${id}`} data-event='click focus'
                                     className="dashboard-tooltip__clickable"
                                     >
                                            <ReactTooltip
                                                isCapture={true}
                                                id={`tooltip-${id}`}
                                                globalEventOff='click'
                                                getContent={() => {
                                                    return (
                                                        <div className="dashboard-tooltip__content">
                                                            {`${mapT(m.METRIC_TO_TOOLTIP, meta.name, companyName)}`}
                                                        </div>
                                                    );}}
                                                className="dashboard-tooltip-custom-class"
                                                arrow
                                                place="top"
                                                textColor='#322352'
                                                backgroundColor='#FFF'
                                                effect='solid'
                                                />
                                        <span>
                                        {
                                            mapT(m.METRIC, mapMetaKpiLabels(companyName, meta.name), companyName)
                                        }
                                        </span>
                                    </a>
                                    {/* </DashboardTooltip> */}
                                        </th>)) :
                                null
                                }
                            </tr>
                        </thead>


                        <tbody className="datatable__scrollable-table-body">
                        {data.length ? data.map((metric, id) => {
                            let title;

                            if (metric.title === "Industry Average") {
                                title = mapT(m.ANALYZE_HEATMAP_SIDEBAR,
                            mapChoiceLabel(industryOrRegional(metric.title,this.props.companyName)), companyName);
                            } else {
                                title = mapT(m.ANALYZE_HEATMAP_SIDEBAR, metric.title);
                            }

                            const formatTitle = (title) => {
                               if (_.isEmpty(title)) { return null; }
                               return title.replace("/", " / ")
                            }

                            const getDominantGroupLabel = (title) => (
                                <div style={{display: "flex", width: "100%", paddingLeft: "8px"}}>
                                    {formatTitle(getDominantGroupCellLabel(companyName, title))}
                                    <div className="icon-question-mark-wrapper" style={{display: "flex", alignItems: "center", textTransform: "none", marginLeft: "5px"}}>
                                        <DashboardTooltip content={this.getDgTooltipContent(companyName)}>
                                            <img className="icon icon-question-mark" style={{height: "15px", width: "15px"}}/>
                                        </DashboardTooltip>
                                    </div>
                                </div>
                            )

                            return (
                                <tr id="xLabel-cell" key={id}>
                                    <td>
                                        {
                                            title == mapT(m.ANALYZE_HEATMAP_SIDEBAR, "Dominant Group") ?
                                            getDominantGroupLabel(title) :
                                            formatTitle(title)
                                        }
                                    </td>
                                    {COLUMN_KEYS.map(key => (_makeCell(
                                        metric,
                                        prevData && prevData[id] ? prevData[id] : null,
                                        key,
                                        id,
                                        disableMyKpi)))}
                                </tr>
                            )
                        }) : null }
                        </tbody>
                    </Table>
                </div>
                <div>
                    <Legend/>
                    {
                        tab === "overview" && <FiltersSelected
                        values={this.state.selectedFilters}
                        allFilters={this.props.allFilters}
                            companyName={this.props.companyName}
                        />
                    }
                </div>
            </div>
            </MainCard>
        );
    }
};

export default withTranslation()(HeatMap);
